import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, get, send, type Me } from "./api.js";
import { rememberAuthTutorIdentity } from "./authIdentity.js";
import { clearLessonDraftsForAccount } from "./privateLessonDraft.js";
import { clearMessagePushAccount, setMessagePushAccount } from "./push/messagePush.js";

interface AuthState {
  me: Me | null;
  /** Distinct from "signed out": we do not yet know which it is. */
  loading: boolean;
  /**
   * A bootstrap failure that did not prove the session ended.
   *
   * Kept separate from `me === null`: a network/5xx failure must never turn a
   * valid rolling session into a password prompt. Only an explicit 401 does.
   */
  bootstrapError: string | null;
  retryBootstrap: () => Promise<void>;
  /** Deliberately stop treating bootstrap as unknown and show the sign-in form. */
  continueToSignIn: () => void;
  /**
   * `identifier` is an email address or a username; the server decides which.
   *
   * Resolves with the account it published so the caller can decide where to
   * land without waiting for a re-render to see `me`.
   */
  signIn: (identifier: string, password: string) => Promise<Me>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const BOOTSTRAP_ERROR =
  "We couldn't confirm your sign-in. Your session may still be active — check your connection and try again.";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const bootstrapAttempt = useRef(0);
  const publishedUserId = useRef<string | null>(null);

  // Seed the tutor identity cache before publishing the new auth value. Child
  // pages render from `me`, so doing this in a later effect would leave a small
  // race in which their first scheduling read still hit the old endpoint.
  const publishMe = useCallback((value: Me | null) => {
    // A null publish (401 on bootstrap or focus refresh, or choosing sign-in)
    // is not evidence the person left: the same account may sign back in and
    // must find its unsaved private draft. The last account stays remembered
    // so a *different* account signing in afterwards still clears it. Explicit
    // sign-out clears in signOut.
    const nextUserId = value?.user.id ?? null;
    const previousUserId = publishedUserId.current;
    if (nextUserId !== null) {
      if (previousUserId && previousUserId !== nextUserId) {
        clearLessonDraftsForAccount(previousUserId);
      }
      publishedUserId.current = nextUserId;
    }
    rememberAuthTutorIdentity(value);
    setMessagePushAccount(nextUserId);
    setMe(value);
  }, []);

  /**
   * Establish whether the browser still has a live Hub session.
   *
   * There are three materially different answers:
   * - 200: publish the authenticated account;
   * - 401: the session really is gone, so show sign-in;
   * - anything else: authentication is unknown, not false. Keep the password
   *   screen away and let the person retry once the network/server recovers.
   *
   * The attempt id makes retry safe if an earlier request completes late. It
   * also replaces the old effect-local cancellation flag, so an unmount or a
   * second attempt cannot publish stale auth state.
   */
  const retryBootstrap = useCallback(async () => {
    const attempt = ++bootstrapAttempt.current;
    setLoading(true);
    setBootstrapError(null);

    try {
      const value = await get<Me>("/auth/me");
      if (attempt === bootstrapAttempt.current) publishMe(value);
    } catch (cause) {
      if (attempt !== bootstrapAttempt.current) return;

      if (cause instanceof ApiError && cause.status === 401) {
        publishMe(null);
        return;
      }

      setBootstrapError(BOOTSTRAP_ERROR);
    } finally {
      if (attempt === bootstrapAttempt.current) setLoading(false);
    }
  }, [publishMe]);

  /**
   * A bootstrap error says only that we could not establish auth state. Someone
   * who knows they are signed out must still be able to enter credentials even
   * if `/auth/me` itself is unhealthy while `/auth/login` is available.
   *
   * This is an explicit user choice, unlike a network/5xx response, so it is
   * safe to move to the signed-out UI. Invalidate any bootstrap request first
   * so a late response cannot replace the form they deliberately asked for.
   */
  const continueToSignIn = useCallback(() => {
    bootstrapAttempt.current += 1;
    setLoading(false);
    setBootstrapError(null);
    publishMe(null);
  }, [publishMe]);

  useEffect(() => {
    void retryBootstrap();
    return () => {
      // Invalidate any request still in flight rather than letting it publish
      // into a provider that has already gone away.
      bootstrapAttempt.current += 1;
    };
  }, [retryBootstrap]);

  /**
   * Read `/me` again when the tab comes back to the front.
   *
   * Capabilities are read fresh on every outbound booking call, so a grant
   * takes effect on the server the moment it is made. The browser was the half
   * that lagged: this was fetched once at startup and never again, so an owner
   * granting `reports.payments.read` to somebody sitting at the desk changed
   * nothing on the screen in front of them, and a *withdrawal* was worse — the
   * sidebar kept offering panels that then answered 403.
   *
   * Focus is the right moment because it is when the two people involved are
   * done talking: the grant is made on one screen and the other person turns
   * back to theirs. Polling would ask hundreds of times a day for an answer
   * that changes a handful of times a year.
   *
   * Only while signed in. Signed out there is nothing on screen that could be
   * stale — the sign-in page is all that is drawn, and signing in reads `/me`
   * itself — so refetching would buy a 401 every time somebody alt-tabs.
   */
  const refreshing = useRef<number | null>(null);
  const signedIn = me !== null;
  useEffect(() => {
    if (!signedIn) return;

    async function refresh() {
      // Two events for one gesture: `visibilitychange` catches a tab being
      // switched to, `focus` catches the whole window coming forward. Either
      // can fire alone, both can fire together, so overlapping reads are
      // dropped rather than raced.
      const generation = bootstrapAttempt.current;
      if (refreshing.current === generation || document.visibilityState !== "visible") return;
      refreshing.current = generation;
      try {
        const account = await get<Me>("/auth/me");
        if (generation === bootstrapAttempt.current) publishMe(account);
      } catch (cause) {
        // A 401 is the session having ended somewhere else — signed out in
        // another tab, or expired — and clearing is the honest answer.
        //
        // Anything else is not evidence about the session. A refetch runs
        // against a screen somebody is already using, so a flaky network or a
        // server restart must not throw them out of a form they are halfway
        // through: what is on screen stays until the server actually says
        // otherwise.
        if (generation === bootstrapAttempt.current && cause instanceof ApiError && cause.status === 401) publishMe(null);
      } finally {
        if (refreshing.current === generation) refreshing.current = null;
      }
    }

    const onVisible = () => void refresh();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [signedIn, publishMe]);

  const signIn = useCallback(async (identifier: string, password: string) => {
    bootstrapAttempt.current += 1;
    await send("POST", "/auth/login", { identifier, password });
    // Re-read rather than trusting the login response: /me is the shape the
    // rest of the app expects, and it is the server's view, not ours.
    const account = await get<Me>("/auth/me");
    publishMe(account);
    setBootstrapError(null);
    return account;
  }, [publishMe]);

  const signOut = useCallback(async () => {
    // Invalidate before the network round trip, not after React renders logout.
    bootstrapAttempt.current += 1;
    void clearMessagePushAccount();
    await send("POST", "/auth/logout");
    bootstrapAttempt.current += 1;
    setBootstrapError(null);
    const departing = publishedUserId.current;
    publishedUserId.current = null;
    if (departing) clearLessonDraftsForAccount(departing);
    publishMe(null);
  }, [publishMe]);

  return (
    <AuthContext.Provider
      value={{
        me,
        loading,
        bootstrapError,
        retryBootstrap,
        continueToSignIn,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

/**
 * Read auth state when a shared leaf component is mounted inside the app, but
 * remain inert when that same component is rendered on its own (for example in
 * isolated component tests). Permissioned actions must still require a real
 * signed-in `me` value before they are shown.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

export function isStaff(me: Me | null): boolean {
  return me !== null && (me.roles.includes("owner") || me.roles.includes("tutor"));
}

/**
 * Whether this account may change things that belong to the whole school.
 *
 * The server decides; this only decides what to draw. Offering a button that
 * always returns 403 is worse than not offering it.
 */
export function isOwner(me: Me | null): boolean {
  return me !== null && me.roles.includes("owner");
}

/** Whether the server says this account owns the separate School domain. */
export function canManageSchoolDomain(me: Me | null): boolean {
  return me?.authorities?.schoolDomain === true;
}

/**
 * Whether the booking system grants this staff account the settings screens.
 *
 * Like the checks above these only decide which links and routes to draw;
 * FastAPI re-checks the capability on every call it receives.
 */
export function canManageLessonBlocks(me: Me | null): boolean {
  return me?.booking.staff === true && me.capabilities.includes("lesson_blocks.manage.all");
}

export function canManageAutomations(me: Me | null): boolean {
  return me?.booking.staff === true && me.capabilities.includes("automations.manage");
}
